import {
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButtonSection,
  IconSizeEnum,
  ModalDialog,
  ModalMessage,
  ModalMessageColorsEnum,
  PlusIcon,
  Typography,
} from '@acceso/design-system';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { TableWithDetailCard } from '@/components/TableWithDetailCard/TableWithDetailCard';
import { FormTypeEnum } from '@/types';

import { useGetPrograms } from '../api/get-programs';
import { ProgramBroadcastGenerationModal } from '../components/ProgramBroadcastGenerationModal';
import { ProgramDetailCard } from '../components/ProgramDetailCard';
import { ProgramDrawerForm } from '../components/ProgramDrawerForm';
import { ProgramTable } from '../components/ProgramTable';
import { useProgramBroadcastGenerationController } from '../hooks/useProgramBroadcastGenerationController';
import { useProgramControllers } from '../hooks/useProgramControllers';
import { useProgramsPermissions } from '../hooks/useProgramsPermissions';
import { useProgramFilters } from '../store/program-filters';
import { TProgram } from '../types';

export default function ProgramPage() {
  const { t } = useTranslation();
  const {
    programDialog,
    programDrawer,
    isPendingCreate,
    handleDeleteProgram,
    handleCreateProgram,
  } = useProgramControllers();
  const { resetFilters } = useProgramFilters();
  const {
    generationModal,
    openGeneration,
    handleGenerate,
    onCancelGeneration,
    onResultStale,
    isRunning: isGenerating,
    progress: generationProgress,
    result: generationResult,
  } = useProgramBroadcastGenerationController();

  /**
   * Resets table filters on page unmount.
   */
  useEffect(() => {
    return () => {
      resetFilters();
    };
  }, [resetFilters]);

  const { data: allPrograms, isFetching, isError } = useGetPrograms({ isProgramPage: true });

  const { canCreate, canUpdate, canDelete, canCreateProgramBroadcast } = useProgramsPermissions();

  const onCreate = () => {
    if (!canCreate) return;
    programDrawer.open({ type: FormTypeEnum.CREATE, data: null });
  };

  const onUpdate = (program: TProgram) => {
    if (!canUpdate) return;
    programDrawer.open({ type: FormTypeEnum.UPDATE, data: program });
  };

  const onDelete = (program: TProgram) => {
    if (!canDelete) return;
    programDialog.open(program);
  };

  const onGenerateBroadcasts = (program: TProgram) => {
    if (!canCreateProgramBroadcast) return;
    openGeneration(program);
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="flex justify-between py-size-8">
          <Typography
            fontSize={FontSizeEnum.Xl}
            weight={FontWeightEnum.Bold}
            color={FontColorEnum.Dark10}
          >
            {t('Programas')}
          </Typography>

          {canCreate && (
            <IconButtonSection
              className="mr-size-16"
              onClick={onCreate}
              internalProps={{
                icon: {
                  size: IconSizeEnum.SM,
                  type: PlusIcon,
                },
                text: {
                  value: t('Nuevo'),
                  color: FontColorEnum.Default,
                },
              }}
            />
          )}
        </div>

        <TableWithDetailCard<TProgram>
          isError={isError}
          renderTable={({ handleOpenSecondCard }) => (
            <ProgramTable
              items={allPrograms}
              onUpdate={canUpdate ? onUpdate : undefined}
              onDelete={canDelete ? onDelete : undefined}
              onGenerateBroadcasts={canCreateProgramBroadcast ? onGenerateBroadcasts : undefined}
              onSelect={handleOpenSecondCard}
              isFetching={isFetching}
            />
          )}
          renderDetailCard={({ data, isSecondCardCollapsed, handleSecondCardCollapse }) => (
            <ProgramDetailCard
              program={data}
              isCollapsed={isSecondCardCollapsed}
              onCollapse={handleSecondCardCollapse}
              onUpdate={onUpdate}
            />
          )}
        />
      </div>

      <ProgramDrawerForm
        isOpen={programDrawer.isOpen}
        onClose={programDrawer.close}
        onSubmit={handleCreateProgram}
        data={programDrawer.data || undefined}
        isMutating={isPendingCreate}
      />

      <ProgramBroadcastGenerationModal
        isOpen={generationModal.isOpen}
        program={generationModal.data}
        onClose={generationModal.close}
        onGenerate={handleGenerate}
        onCancel={onCancelGeneration}
        onResultStale={onResultStale}
        isRunning={isGenerating}
        progress={generationProgress}
        result={generationResult}
      />

      <ModalDialog
        isOpen={programDialog.isOpen}
        onClose={programDialog.close}
        onAction={handleDeleteProgram}
        heading={t('Eliminar programa')}
        actionLabel={t('Eliminar')}
        linkLabel={t('Cancelar')}
        color={ModalMessageColorsEnum.ERROR}
      >
        <ModalMessage title={t('¿Estás seguro de querer eliminar el programa seleccionado?')} />
      </ModalDialog>
    </>
  );
}
